import { GCSEOHead, CTABanner, Breadcrumbs, RelatedGuides, LastUpdated } from "./GCLayout";
import { gcCanonicalBase } from "./gcDomain";
import { JsonLd } from "../../components/JsonLd";
import { ArrowRight } from "lucide-react";

const TIMELINE_DATA = [
  {
    category: "Family-Based (Immediate Relative)",
    steps: [
      { step: "File I-130 Petition", duration: "0 months", note: "Petition filed by U.S. citizen spouse, parent, or child over 21" },
      { step: "I-130 Approval", duration: "6-12 months", note: "USCIS processes the petition" },
      { step: "File I-485 or DS-260", duration: "Immediately after", note: "Can file concurrently with I-130" },
      { step: "Biometrics Appointment", duration: "1-2 months after filing", note: "Fingerprints and photos taken" },
      { step: "Interview", duration: "8-14 months after I-485 filing", note: "At local USCIS office or consulate" },
      { step: "Green Card Received", duration: "12-18 months total", note: "Card mailed within 2-3 weeks of approval" },
    ],
  },
  {
    category: "Family-Based (Preference Categories)",
    steps: [
      { step: "File I-130 Petition", duration: "0 months", note: "For married children, siblings, etc." },
      { step: "I-130 Approval", duration: "6-12 months", note: "" },
      { step: "Wait for Priority Date", duration: "2-23+ years", note: "Check monthly Visa Bulletin from DOS" },
      { step: "File I-485 / DS-260", duration: "When date is current", note: "" },
      { step: "Interview", duration: "6-12 months after filing", note: "" },
      { step: "Green Card Received", duration: "3-24+ years total", note: "Varies significantly by country and category" },
    ],
  },
  {
    category: "Employment-Based (EB-1)",
    steps: [
      { step: "File I-140 Petition", duration: "0 months", note: "No PERM required for EB-1A/EB-1B" },
      { step: "I-140 Approval", duration: "4-12 months (15 days premium)", note: "Premium processing available" },
      { step: "File I-485", duration: "Immediately if current", note: "Priority dates usually current" },
      { step: "Biometrics & Interview", duration: "6-12 months", note: "Interview may be waived" },
      { step: "Green Card Received", duration: "12-24 months total", note: "" },
    ],
  },
  {
    category: "Employment-Based (EB-2/EB-3)",
    steps: [
      { step: "PERM Labor Certification", duration: "8-18 months", note: "Employer proves no U.S. workers available" },
      { step: "File I-140 Petition", duration: "After PERM approval", note: "Premium processing: 15 days" },
      { step: "Wait for Priority Date", duration: "0-10+ years", note: "India/China backlogs are severe" },
      { step: "File I-485", duration: "When date is current", note: "" },
      { step: "Green Card Received", duration: "2-12+ years total", note: "Highly dependent on country of birth" },
    ],
  },
  {
    category: "Diversity Visa Lottery",
    steps: [
      { step: "Submit DV Lottery Entry", duration: "Oct-Nov registration", note: "Free entry at dvprogram.state.gov" },
      { step: "Check Results", duration: "May of following year", note: "~100K selected from ~15M entries" },
      { step: "Submit DS-260", duration: "Immediately after selection", note: "Immigrant visa application" },
      { step: "Gather Documents", duration: "1-3 months", note: "Passport, civil docs, medical exam" },
      { step: "Consular Interview", duration: "As scheduled by embassy", note: "Must complete before Sept 30" },
      { step: "Enter U.S. & Receive Card", duration: "12-18 months total", note: "Card mailed after entry" },
    ],
  },
  {
    category: "EB-5 Investment",
    steps: [
      { step: "Make Qualifying Investment", duration: "Variable", note: "$800K (TEA) or $1.05M standard" },
      { step: "File I-526E Petition", duration: "After investment", note: "" },
      { step: "I-526E Approval", duration: "24-48 months", note: "Processing times vary widely" },
      { step: "File I-485 / Consular Process", duration: "After approval", note: "" },
      { step: "Receive Conditional Card", duration: "3-5 years total", note: "2-year conditional residence" },
      { step: "File I-829 to Remove Conditions", duration: "Before card expires", note: "Prove job creation requirements met" },
    ],
  },
];

const TIMELINE_FAQS = [
  {
    q: "Why is my Green Card taking so long?",
    a: "The most common reasons are per-country visa backlogs (especially for India and China), USCIS processing delays, requests for additional evidence (RFEs), and security background checks. Employment-based applicants from backlogged countries can wait 5-10+ years for a priority date to become current.",
  },
  {
    q: "Can I speed up my Green Card processing?",
    a: "For I-140 petitions, you can request premium processing (15 calendar days) for an additional $2,805 fee. For I-485 and other forms, there is no formal way to expedite. You can contact USCIS or your congressional representative if your case is significantly outside normal processing times.",
  },
  {
    q: "What is premium processing?",
    a: "Premium processing is a USCIS service that guarantees a response on certain petitions (mainly I-140) within 15 calendar days. It costs $2,805 and results in either an approval, denial, notice of intent to deny, or request for evidence — not necessarily an approval.",
  },
  {
    q: "How do I check my Green Card application status?",
    a: "You can check your case status online at egov.uscis.gov/casestatus using the receipt number from your filing. USCIS also sends paper notices for each major milestone. Processing times for each form are published at egov.uscis.gov/processing-times.",
  },
];

export default function GCTimelinePage() {
  const base = gcCanonicalBase();

  const howToSteps = TIMELINE_DATA[0].steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.step,
    text: s.note || s.step,
  }));

  return (
    <>
      <GCSEOHead
        title="Green Card Processing Times 2026: Realistic Wait by Category"
        description="How long does a Green Card take in 2026? Realistic processing timelines for family-based, employment-based, DV Lottery, and EB-5 categories broken down step by step."
        path="/green-card-timeline"
      />

      {/* JSON-LD HowTo Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Get a U.S. Green Card (Family-Based Immediate Relative)",
            description: "Step-by-step timeline for obtaining a U.S. Green Card through family-based immigration as an immediate relative.",
            dateModified: "2026-02-01",
            url: `${base}/green-card-timeline`,
            totalTime: "P18M",
            step: howToSteps,
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: TIMELINE_FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* Breadcrumbs */}
      <Breadcrumbs current="Processing Timeline" />

      {/* Header */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-4" data-testid="gc-timeline-h1">
            Green Card Processing Times (2026)
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
            "How long will my Green Card take?" is the first question everyone asks. The honest answer:
            it depends on your category, country of birth, and whether USCIS is backlogged. Below are
            realistic timelines for every major pathway, broken down step by step.
          </p>
          <div className="mt-4">
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* Featured Snippet Box */}
      <section className="py-6 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-emerald-50 rounded-lg p-5 border border-emerald-100" data-testid="gc-timeline-snippet">
            <h2 className="font-semibold text-slate-900 text-sm mb-2">How long does a Green Card take?</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              <strong>Immediate relatives</strong> of U.S. citizens: 12-18 months.{" "}
              <strong>Employment-based EB-1</strong>: 1-3 years.{" "}
              <strong>EB-2/EB-3</strong>: 2-12+ years (longer for India/China).{" "}
              <strong>DV Lottery</strong>: ~12-18 months after selection.{" "}
              <strong>EB-5 Investment</strong>: 3-5+ years.
            </p>
          </div>
        </div>
      </section>

      {/* Timelines */}
      <section className="py-12 md:py-16" data-testid="gc-timeline-tables">
        <div className="max-w-5xl mx-auto px-4 space-y-12">
          {TIMELINE_DATA.map((t) => (
            <div key={t.category}>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading mb-4">
                {t.category}
              </h2>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm" data-testid={`gc-timeline-table-${t.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 w-12">#</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Step</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 whitespace-nowrap">Timeline</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden md:table-cell">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {t.steps.map((s, j) => (
                      <tr key={j} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">
                            {j + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">{s.step}</td>
                        <td className="px-4 py-3 text-emerald-700 font-medium whitespace-nowrap">{s.duration}</td>
                        <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{s.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-amber-800">
            <strong>Disclaimer:</strong> These timelines are estimates based on recent USCIS processing data.
            Actual times vary based on case complexity, USCIS workload, country of birth, and individual circumstances.
            Check <a href="https://egov.uscis.gov/processing-times/" target="_blank" rel="noopener noreferrer" className="underline">USCIS Processing Times</a> for current data.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16" data-testid="gc-timeline-faq">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 font-heading mb-6">
            Processing Timeline FAQ
          </h2>
          <div className="space-y-6">
            {TIMELINE_FAQS.map((faq) => (
              <div key={faq.q}>
                <h3 className="font-semibold text-slate-900 text-sm mb-1">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Soft CTA */}
      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-600 mb-4 text-base">
            Want to track your own timeline and get deadline reminders?
          </p>
          <a
            href="https://immigrationguide.us/auth?mode=register"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
            data-testid="gc-timeline-cta"
          >
            Track your case for free <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* DV Lottery Resources */}
      <section className="py-10 border-t border-slate-100" data-testid="gc-timeline-dv-resources">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 font-heading mb-4">DV Lottery Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="https://immigrationguide.us/how-to-check-dv-lottery-results" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">How to Check DV Lottery Results</h3>
              <p className="text-xs text-slate-500">Official steps and deadlines for the 2026 cycle</p>
            </a>
            <a href="https://immigrationguide.us/dv-lottery-eligibility" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">DV Lottery Eligibility</h3>
              <p className="text-xs text-slate-500">Country, education, and work experience requirements</p>
            </a>
          </div>
        </div>
      </section>

      <RelatedGuides currentPath="/green-card-timeline" />
      <CTABanner />
    </>
  );
}
